

const enOrdinalRules = new Intl.PluralRules("en-US", { type: "ordinal" })

const suffixes = new Map([
  ["one", "pair of"],
  ["two", "pairs of"],
  ["few", "pairs of"],
  ["other", "pairs of"],
])

function formatBundleQuantity (total) {
  const n = total / 2
  if (n % 1) return `${ total }x`
  const rule = enOrdinalRules.select(n)
  const suffix = suffixes.get(rule)
  return `${ n } ${ suffix }`
}

function formatPairingQuantity (total) {
  const n = total / 2
  if (n % 1) return `${ total } Single`
  return `Pair`
}

function formatMoney (value) {
  if (typeof value === 'string') return value
  if (window.Shopify && window.Shopify.formatMoney) return window.Shopify.formatMoney(value)
  // Fallback
  const floatPrice = value / 100
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(floatPrice)
}

const optionKeys = {
  /** The option name used to check if a product has a personalization option */
  engraving: 'Engraving',
  material: 'Material',
  size: 'Size',
  style: 'Style'
}

const materialValues = {
  sterlingSilver: 'Sterling Silver',
  gold10K: '10K Gold',
  gold14K: '14K Gold'
}
const secondaryMaterials = ['Cavan Gold', 'Cavan Rose Gold']

/**
 * Texes Exes are (currently) the only products that _only_ have F+B
 * engraving, so we set some overrides where if we are that product,
 * then the shipping badge fn always checks for the F+B variants
 * inventory, instead of the front.
 */
const inventoryVariantsOverrides = new Map([
  [40893093281894, { nonPersonalized: 40893093281894, sterlingSilver: 40893093281894 }],
  [40893093347430, { nonPersonalized: 40893093347430, sterlingSilver: 40893093281894 }],
  [40893093412966, { nonPersonalized: 40893093412966, sterlingSilver: 40893093281894 }]
])

/** As we edit search params, we keep a copy of the original */
const { searchParams: originalSearchParams } = new URL(window.location.href)

// TODO: If we need to use custom f+b option names, then we'll need some metadata to let us know exactly what side is what.
// Then we can use those as initial values, and the below as defaults for those that don't have them.

/** Const keys for engraving related operations. */
const engravingKeys = /** @type {const} */ ({
  /** The option values used to find the personalized version of a SKU */
  options: {
    front: 'Front Engraving',
    back: 'Back Engraving',
    frontBack: 'Front & Back Engraving',
    frontPreset: 'Front & Preset Back Engraving'
  },
  sides: {
    front: 'Front Side',
    back: 'Back Side',
    frontBack: 'Front & Back Sides'
  }
})

// Thumbnail slider
// const thumbnails = document.getElementById('thumbnails')
// if (thumbnails.clientHeight < thumbnails.scrollHeight) document.getElementById('scroll-arrow').style.display = 'block'

/** Initialize the product app with a specific element and default props. */
function initProductApp (el = '#product', props) {
  const { product, productOptions, metadata, timelines, productStrings, bundles = [], chain, sizeGuides = [], chainSizeGuide } = props
  console.debug(`Product app \`${ el }\` initialised with:`, window.__productData__)

  const defaultEngravingValue = engravingKeys.options.frontBack
  const engravingTypes = [optionKeys.engraving, metadata.defaults.engraved_variant || defaultEngravingValue]

  const materialOption = productOptions.find(({ name }) => name === optionKeys.material)
  const engravingOption = productOptions.find(({ name }) => name === optionKeys.engraving)

  const { searchParams } = new URL(window.location.href)

  // We use this even if we are in a bundle, as we still select the primary image
  const initialSelectedVariant = searchParams.has('variant') && product.variants.find(variant => variant.id.toString() === searchParams.get('variant'))
  const initialAvailableVariant = product.variants.find(variant => variant.available && !engravingTypes.some(type => variant.options.includes(type)))
  const fallbackVariant = product.variants[0]

  const initialVariant = initialSelectedVariant || initialAvailableVariant || fallbackVariant

  const currentImageIndex = initialVariant.featured_image && !bundles.length ? initialVariant.featured_image.position - 1 : 0

  const selectedOptions = Object.fromEntries(initialVariant.options.map((value, i) => {
    const option = productOptions.find(option => option.position === (i + 1))
    return [option.name, value]
  }))

  const options = productOptions.filter(option => option.name !== optionKeys.engraving)

  const formattedBundles = bundles.map(bundle => {
    const items = bundle.items.map(item => {
      const optionsWithValues = item.type === 'product' ? item.resource.options.map((option, i) => {
        const allValues = item.resource.variants.map(variant => variant.options[i])

        return { name: option, position: i + 1, values: Array.from(new Set(allValues)) }
      }) : item.parent.options.map((option, i) => ({ name: option, position: i, values: [item.resource.options[i]] }))

      const isFixedSize = option => option.name === optionKeys.size && option.values.length === 1
      const optionsToShow = optionsWithValues.filter(option => !engravingTypes.includes(option.name) && !isFixedSize(option))
      const fixedSize = optionsWithValues.find(isFixedSize)?.values[0]

      const selectedOptions = optionsWithValues.map((option, i) => {
        if (option.values.length === 1) return [option.name, option.values[0]]

        // Handle engraving
        if (engravingTypes.includes(option.name)) return [option.name, engravingKeys.options.front]

        return [option.name, option.values[0]]
      })

      const quantityText = formatBundleQuantity(item.quantity)
      const pairingText = formatPairingQuantity(item.quantity)

      const metadata = item.metadata.map(([key, ...values]) => [key, Object.assign({}, ...values)])

      return {
        ...item,
        title: item.type === 'product' ? item.resource.title : item.parent.title,
        fixedSize,
        metadata: new Map(metadata),
        quantityText,
        pairingText,
        optionsWithValues,
        optionsToShow,
        selectedOptions: Object.fromEntries(selectedOptions)
      }
    })
    return { ...bundle, items }
  })

  const initialSelectedBundleOptions = formattedBundles.map((bundle, i) => {
    const selectedOptions = bundle.items.map((item, i) => [i, item.selectedOptions])
    return [i, Object.fromEntries(selectedOptions)]
  })

  const initialSelectedCustomizations = {
    personalization: false, // Is personalization available
    chain: false, // Is chain available
    // Chain options
    chainMaterial: selectedOptions[optionKeys.material] || null,
    chainSize: null,
    chainStyle: null,
    /** Whether to choose single or pair */
    pairingOption: 'pair'
  }

  const initial = JSON.parse(JSON.stringify({
    selectedOptions: selectedOptions,
    selectedBundleOptions: Object.fromEntries(initialSelectedBundleOptions)
  }))

  return new Vue({
    el: '#product',
    delimiters: ['${', '}'],
    filters: {
      money: formatMoney
    },
    components: {
      GalleryLightbox: window.Components.GalleryLightbox
    },
    data: () => {
      return {
        product,
        options,
        optionsWithValues: productOptions,
        bundles: formattedBundles,
        chainShowOptionImages,
        state: { loading: false, success: false, error: false },
        showScrollArrow: false,
        currentImageIndex,
        lightboxActive: false,
        quantity: 1,
        email: '',
        giftnote: '',
        giftcard: {
          sendToSelf: false,
          name: null,
          email: null,
          date: null,
          message: '',
          invalid: false
        },
        selectedOptions,
        selectedBundleOptions: Object.fromEntries(initialSelectedBundleOptions),
        selectedCustomizations: initialSelectedCustomizations,
        activeActionSection: '',
        activeInformationSection: 'description',
        waitlistActive: false,
        // A reference to the initial options, if we want to reset
        initial,
        toggles: {
          shippingBadgeTooltip: false,
          sizeGuide: false,
          chainSizeGuide: false
        },
        previewsActive: {
          chainStyle: false,
        },
        isPressed: false,
        /** Holds page specific inventory - currently used for our incoming inventory checks. */
        inventory: {
          loading: false,
          incoming: new Map()
        }
      }
    },
    computed: {
      // IS
      isBundleProduct () {
        return !!this.bundles.length
      },
      isPairProduct () {
        // We are using tags for now, but we may want to update this in the future
        return metadata.defaults && metadata.defaults.pairing_option
      },
      isFreeGiftProduct () {
        return window.cartConfig.enableFreeGift && window.cartConfig.freeGiftProduct && window.cartConfig.freeGiftProduct.id === this.currentVariant.id
      },
      isPreorderProduct () {
        return Boolean(metadata.details.preorder_item)
      },
      isOnSale () {
        return this.totalPrice > this.discountedPrice
      },
      isPersonalizable () {
        if (this.currentVariant.compare_at_price && this.currentVariant.compare_at_price > this.currentVariant.price) return false

        const { enabledFrontEngraving, enabledBackEngraving } = this.personalizationVariants || {}
        return Boolean(enabledFrontEngraving || enabledBackEngraving)
      },
      isChainAvailable () {
        const { defaults, variants } = metadata
        const currentVariant = this.currentVariant
        if (!currentVariant) return false

        const chain = variants.get(currentVariant.id)?.chain || defaults.chain
        return chain && chain === 'Enabled'
      },
      isPersonalizationPresetSelected () {
        return this.selectedOptions[optionKeys.engraving] === engravingKeys.options.frontPreset
      },
      // CAN
      canCheckout () {
        if (this.isPairProduct) {
          if (this.selectedCustomizations.pairingOption !== 'pair') return this.currentVariant.available

          const inventory = window.__initialData__.inventories.get(this.currentVariant.id)
          if (!inventory) return this.currentVariant.available

          if (inventory.policy === 'continue') return true
          return this.currentVariant.available && inventory.quantity >= 2
        }

        if (this.isBundleProduct) {
          return this.selectedBundleProducts.every(variant => {
            if (!variant.available) return false

            const inventory = window.__initialData__.inventories.get(variant.id)
            if (!inventory) return item.available
            if (inventory.policy === 'continue') return true

            return inventory.quantity >= variant.quantity
          })
        }

        return this.currentVariant.available
      },
      // HAS
      // If the engraving options has a preset option that we should show.
      // We may also want this to return the option value, if it ever changes
      hasEngravingPresetOption () {
        return engravingOption && engravingOption.values.includes(engravingKeys.options.frontPreset)
      },
      // SHOW
      showVariants () {
        return !this.isFreeGiftProduct
      },
      showChainOption () {
        if (this.isBundleProduct) return false

        return true
      },
      /** Enable showing chain material, if enabled in the metafield */
      showChainMaterialOption () {
        return !!metadata.defaults.chain_material_enabled
      },
      // COMPUTED
      appliedDiscounts () {
        if (!productDiscounts) return []

        // if (this.isOnSale) return []

        // TODO: Work out if we allow this? If not, we likely want to sort by value, and add an option to disable combining
        const firstDiscount = productDiscounts.find(discount => {
          if (discount.bundles && !this.isBundleProduct) return false

          const isExcluded = discount.excluded_products.some(product => product.id === this.product.id)
          if (isExcluded) return false

          const conditions = discount.conditions.split('\n').map(str => str.toLowerCase().trim())
          const titleIncludes = conditions.some(str => this.product.title.toLowerCase().includes(str))
          const familyIncludes = conditions.some(str => metadata.family && metadata.family.toLowerCase().includes(str))

          return titleIncludes || familyIncludes
        })

        return firstDiscount ? [firstDiscount] : []
      },

      matchingSizeGuide () {
        const familyMatch = metadata?.family && sizeGuides.find(guide => guide.filters?.families?.some(str => str.toLowerCase() === metadata?.family.toLowerCase()))
        const tagMatch = product?.tags && sizeGuides.find(guide => guide.filters?.tags?.some(tag => product.tags.some(str => str.toLowerCase() === tag.toLowerCase())))
        const typeMatch = product?.type && sizeGuides.find(guide => guide.filters?.productTypes?.some(str => str.toLowerCase() === product.type.toLowerCase()))

        const selectedMatch = familyMatch || tagMatch || typeMatch
        if (!selectedMatch) {
          return
        }

        return selectedMatch
      },
      matchingChainSizeGuide () {
        // Only show if we have a chain option
        if (!this.isChainAvailable) return

        return chainSizeGuide
      },
      // Used in the materials tab
      materials () {
        if (!materialOption) return []
        return productMaterials.filter(([name]) => materialOption.values.includes(name))
      },
      currentVariant () {
        const selectedOptions = this.selectedOptions

        const [firstVariant] = product.variants
        if (!Object.keys(selectedOptions).length) return firstVariant

        const variant = product.variants.find(variant => variant.options.every((value, i) => {
          const option = productOptions.find(option => option.position === (i + 1))
          if (!option) return false
          return selectedOptions[option.name] === value
        }))

        return variant
      },
      currentNonPersonalizedVariant () {
        return this.getNonPersonalizedVariant(this.currentVariant)
      },
      inventoryBadge () {
        // We should only show if we are tracking inventory
        if (!this.currentVariant) return
        const inventory = window.__initialData__.inventories.get(this.currentVariant.id)
        return inventory && inventory.quantity < 4 && 'Only a few left!'
      },
      addToCartText () {
        if (!metadata) return

        if (metadata.details && metadata.details.backorder_text) return metadata.details.backorder_text

        if (metadata.details.preorder_item) return 'Pre-order product'

        if (!this.currentVariant || !metadata.variants) return

        const variantsMetadata = metadata.variants[this.currentVariant.id]
        return variantsMetadata && variantsMetadata.backorder_text
      },
      finalSaleText () {
        if (!metadata) return

        if (metadata.details && metadata.details.final_sale_text) return metadata.details.final_sale_text

        if (!this.currentVariant || !metadata.variants) return

        const variantsMetadata = metadata.variants[this.currentVariant.id]
        return variantsMetadata && variantsMetadata.final_sale_text
      },
      salePercentage () {
        // (Original Price - Final Price) / Original Price x 100
        const percentage = (this.totalPrice - this.discountedPrice) / this.totalPrice * 100
        return Math.round(percentage)
      },
      shippingBadge () {
        const variants = this.isBundleProduct ? this.selectedBundleProducts : [this.currentVariant]
        return this.getShippingBadge(variants)
      },
      personalizationResources () {
        if (this.isBundleProduct) {
          // Apparently we only ever have one personalizable product in a bundle, so find the first match
          const [firstItem] = this.bundles.flatMap((bundle, bundleIndex) => {
            const itemIndex = bundle.items.findIndex(item => item.type === 'product' && item.resource.options.includes(optionKeys.engraving))

            const item = bundle.items[itemIndex]

            return item ? [{ ...item, itemIndex, bundleIndex }] : []
          })

          if (!firstItem) return

          const resourceMetadata = firstItem.metadata.get(firstItem.resource.id)

          const engravingOption = firstItem.optionsWithValues.find(({ name }) => name === optionKeys.engraving)
          const selectedBundleOptions = this.selectedBundleOptions[firstItem.bundleIndex][firstItem.itemIndex]

          const selectedOptions = Object.entries(selectedBundleOptions).flatMap(([key, value]) => {
            if (key === optionKeys.engraving) return []
            const matchingOption = firstItem.optionsWithValues.find(option => option.name === key)
            return matchingOption ? [{ ...matchingOption, selected: value }] : []
          })

          // Matching existing format
          return {
            product: firstItem.resource,
            selectedOptions,
            engravingOption,
            metadata: {
              defaults: resourceMetadata,
              vector: resourceMetadata.personalization_preview
            }
          }
        }

        if (!engravingOption) return

        const selectedOptions = Object.entries(this.selectedOptions).flatMap(([key, value]) => {
          if (key === optionKeys.engraving) return []
          const matchingOption = productOptions.find(option => option.name === key)
          return matchingOption ? [{ ...matchingOption, selected: value }] : []
        })

        // Fallback to product data
        return { product, metadata, selectedOptions, engravingOption }
      },
      personalizationVariants () {
        if (!this.personalizationResources) return

        const { product, metadata, selectedOptions, engravingOption } = this.personalizationResources
        // console.debug(this.personalizationResources)

        const personalizationOptions = metadata.vector && window.personalizationApp.getPreviewOptions(metadata)
        if (!personalizationOptions) {
          console.info(`No personalization options found using metadata:`, metadata)
          return
        }

        const isDisabled = metadata.defaults.personalization !== 'Enabled'
        if (isDisabled) {
          console.warn(`Personalization options found, but personalisation was disabled.`)
          return
        }

        const metadataPersonalizationSide = metadata.defaults.personalization_side
        const allowsFrontEngraving = metadataPersonalizationSide ? metadataPersonalizationSide === 'Front Side' || metadataPersonalizationSide === 'Front & Back Sides' : personalizationOptions.front
        const allowsBackEngraving = metadataPersonalizationSide ? metadataPersonalizationSide === 'Back Side' || metadataPersonalizationSide === 'Front & Back Sides' : personalizationOptions.back

        /** The default front variant option names. */
        const defaultFrontVariantOptions = metadata.details ? [engravingKeys.options.front, metadata.details?.front_variant_option_name] : [engravingKeys.options.front]

        /** The default back variant option names. */
        const defaultBackVariantOptions = [engravingKeys.options.back, engravingKeys.options.frontBack]

        /**
         * Given a variant, and relevant metadata, this function returns two arrays -
         * one for product options to include, and one to exclude.
         *
         * @param {'front' | 'back'} op
         * @param {string | null | undefined} personalizationSide
         * @param {string | null | undefined} engravedVariant
         */
        function getOptionsToCheck (op, personalizationSide, engravedVariant) {
          const sides = op === 'front' ? [engravingKeys.sides.front, engravingKeys.sides.frontBack] : [engravingKeys.sides.back, engravingKeys.sides.frontBack]
          const defaultVariantOptions = op === 'front' ? defaultFrontVariantOptions : defaultBackVariantOptions

          if (!engravedVariant || !metadataPersonalizationSide) {
            return defaultVariantOptions
          }

          // If we match the selected personalization side
          if (sides.includes(personalizationSide)) {
            // We match, so return a direct match on the engraved variant
            return [engravedVariant]
          }

          // Otherwise, make sure we _don't_ use the engraved variant!
          return defaultVariantOptions.filter(option => option !== engravedVariant)

        }

        const frontOptionsToCheck = getOptionsToCheck('front', metadataPersonalizationSide, metadata.defaults.engraved_variant)
        const backOptionsToCheck = getOptionsToCheck('back', metadataPersonalizationSide, metadata.defaults.engraved_variant)

        const frontEngravingVariant = product.variants.find(variant => {
          const variantOptionValue = variant.options[engravingOption.position - 1]
          const hasMatchingEngraving = variantOptionValue && frontOptionsToCheck.includes(variantOptionValue)

          const hasMatchingOptions = selectedOptions.every(option => option.selected === variant.options[option.position - 1])

          return hasMatchingEngraving && hasMatchingOptions
        })

        const backEngravingVariant = product.variants.find(variant => {
          const variantOptionValue = variant.options[engravingOption.position - 1]
          const hasMatchingEngraving = variantOptionValue && backOptionsToCheck.includes(variantOptionValue)

          const hasMatchingOptions = selectedOptions.every(option => option.selected === variant.options[option.position - 1])
          return hasMatchingEngraving && hasMatchingOptions
        })

        const presetEngravingVariant = product.variants.find(variant => {
          const hasMatchingEngraving = variant.options[engravingOption.position - 1] === engravingKeys.options.frontPreset
          const hasMatchingOptions = selectedOptions.every(option => option.selected === variant.options[option.position - 1])
          return hasMatchingEngraving && hasMatchingOptions
        })

        // If we have front + back then set cost = 0, otherwise set to differences in variants?
        const enabledFrontEngraving = Boolean(allowsFrontEngraving && frontEngravingVariant)
        const enabledBackEngraving = Boolean(allowsBackEngraving && backEngravingVariant)
        const enabledPresetEngraving = Boolean(allowsBackEngraving && presetEngravingVariant)

        const personalizationSide = enabledFrontEngraving && enabledBackEngraving
          ? engravingKeys.options.frontBack
          : enabledBackEngraving
            ? engravingKeys.options.back
            : engravingKeys.options.front

        console.debug({
          enabledFrontEngraving,
          enabledBackEngraving,
          enabledPresetEngraving,
          personalizationSide,
          frontEngravingVariant,
          backEngravingVariant,
          presetEngravingVariant,
          personalizationOptions
        })

        return {
          enabledFrontEngraving,
          enabledBackEngraving,
          enabledPresetEngraving,
          personalizationSide,
          frontEngravingVariant,
          backEngravingVariant,
          presetEngravingVariant,
          personalizationOptions
        }
      },
      personalizationCost () {
        if (!this.personalizationVariants) return ''

        const { product } = this.personalizationResources
        const { enabledFrontEngraving, enabledBackEngraving, backEngravingVariant, frontEngravingVariant } = this.personalizationVariants
        console.debug(`[PersonalizationCost] Checking variants:`, { backEngravingVariant, frontEngravingVariant })

        // If we have both enabled, then show the initial cost as free, as it is back that will add the price
        if (enabledFrontEngraving && enabledBackEngraving) return '(Free)'

        // Usually we add back, so we check for that first
        if (enabledBackEngraving) {
          const currentVariant = this.isBundleProduct ? this.getNonPersonalizedVariant(backEngravingVariant, product) : this.currentVariant
          const cost = backEngravingVariant.price - currentVariant.price
          return cost > 0 ? cost : '(Free)'
        }

        // Here we have no back, and we may or may not have front engraving.
        if (enabledFrontEngraving) {
          const currentVariant = this.isBundleProduct ? this.getNonPersonalizedVariant(frontEngravingVariant, product) : this.currentVariant
          const cost = frontEngravingVariant.price - currentVariant.price
          return cost > 0 ? cost : '(Free)'
        }

        // Not sure how we would get here? But a fallback anyway
        return ''
      },
      personalizationPresetCost () {
        const logger = this.createLogger('personalizationPresetCost')
        if (!this.personalizationVariants) return ''

        const { enabledFrontEngraving, enabledPresetEngraving, presetEngravingVariant, frontEngravingVariant } = this.personalizationVariants
        logger(`[PersonalizationPresetCost] Checking variants:`, { presetEngravingVariant, frontEngravingVariant })

        if (!enabledPresetEngraving) return ''

        // Fallback, as some products don't have a front variant
        const comparisonVariant = frontEngravingVariant ?? this.getNonPersonalizedVariant(presetEngravingVariant)
        if (!comparisonVariant) return ''

        const cost = presetEngravingVariant.price - comparisonVariant.price
        return cost > 0 ? cost : '(Free)'
      },
      /**
       * All available chain options. Extracts material, size, and style,
       * which are used to filter down available options.
       */
      availableChainOptionProducts () {
        const logger = this.createLogger('availableChainOptionProducts')
        logger('Context', { isChainAvailable: this.isChainAvailable, chain: chain })

        if (!this.isChainAvailable) return []

        const availableParentMaterials = materialOption ? materialOption.values : []

        const options = chain.products.sort((a, b) => {
          if (!metadata.defaults || !metadata.defaults.chain_product) return 0
          return metadata.defaults.chain_product.includes(a.id)
            ? -1
            : metadata.defaults.chain_product.includes(b.id)
              ? 1
              : 0
        }).flatMap(product => {
          const materialIndex = product.options.indexOf(optionKeys.material)
          const sizeIndex = product.options.indexOf(optionKeys.size)
          const styleIndex = product.options.indexOf(optionKeys.style)

          return product.variants.flatMap(variant => {
            if (!variant.available) return []

            const material = variant.options[materialIndex]
            const size = variant.options[sizeIndex]
            const style = variant.options[styleIndex] || product.title.replace('Chain', '').trim()

            // Only show materials that match the parent item materials
            if (!availableParentMaterials.includes(material)) return []

            return [{
              ...variant,
              type: 'chain',
              image: product.image,
              title: product.title,
              name: `${ style } ${ size }`,
              displayName: `${ material } / ${ style } / ${ size }`,
              options: { material, size, style }
            }]
          })
        })

        logger('Mapped over available chain options:', {options, chain_products: chain.products})

        return options
      },
      /** First option, so show all options */
      chainMaterialOptions () {
        const materialOptions = this.availableChainOptionProducts.reduce((set, variant) => set.add(variant.options.material), new Set())
        return materialOptions
      },
      /** Second option, so show all options that match the material.
       *  Sorted by parsed numeric range (start asc, then end asc) so single-length
       *  chains slot into their correct position relative to multi-length chains —
       *  e.g. "16", "16-17-18", "18", "20-21-22". */
      chainSizeOptions () {
        const sizeOptions = this.availableChainOptionProducts.reduce((set, variant) => {
          if (this.selectedCustomizations.chainMaterial !== variant.options.material) return set
          return set.add(variant.options.size)
        }, new Set())
        const parseRange = (s) => {
          const nums = String(s).match(/\d+/g)?.map(Number) ?? [0]
          return [nums[0], nums[nums.length - 1]]
        }
        return new Set([...sizeOptions].sort((a, b) => {
          const [aStart, aEnd] = parseRange(a)
          const [bStart, bEnd] = parseRange(b)
          return aStart - bStart || aEnd - bEnd
        }))
      },
      /** Final option, so show all options that match material + size */
      chainStyleOptions () {
        // We want the actual options, so we return the filtered items instead of plain values
        const styleOptions = this.availableChainOptionProducts.filter(({ options }) => {
          const matchesMaterial = options.material === this.selectedCustomizations.chainMaterial
          const matchesSize = options.size === this.selectedCustomizations.chainSize
          return matchesMaterial && matchesSize
        })
        return styleOptions
      },
      // Get matching chain products
      selectedCustomizationProducts () {
        const logger = this.createLogger('selectedCustomizationProducts')

        const selected = this.selectedCustomizations

        const chainProduct = this.availableChainOptionProducts.find(({ options }) => {
          const matchesMaterial = options.material === selected.chainMaterial
          const matchesSize = options.size === selected.chainSize
          const matchesStyle = options.style === selected.chainStyle
          return matchesMaterial && matchesSize && matchesStyle
        })

        if (chainProduct) logger(`Matching chain product: ${ chainProduct.displayName }`, chainProduct, { selected })
        else logger('No matching chain product found - returning empty array.')

        return chainProduct ? [chainProduct] : []
      },
      /** Returns a boolean indicating if we are a pair product, and if the pairing option selected is `pair`. */
      isPairSelected () {
        return this.isPairProduct && this.selectedCustomizations.pairingOption === 'pair'
      },
      // Get all bundle products, along with their quantity multiplier
      selectedBundleProducts () {
        if (!this.bundles) return []

        return this.bundles.flatMap((bundle, bundleIndex) => {
          return bundle.items.flatMap((item, itemIndex) => {
            // If we are a variant, return it (as we don't need to find a variant)
            if (item.type === 'variant') return [{ ...item.resource, quantity: item.quantity, bundle, bundleItem: item }]

            const selectedOptions = item.optionsWithValues.map(option => {
              return {
                ...option,
                selected: this.selectedBundleOptions[bundleIndex][itemIndex][option.name]
              }
            })

            const matchingVariant = item.resource.variants.find(variant => {
              return selectedOptions.every(option => option.selected === variant.options[option.position - 1])
            })

            return matchingVariant ? [{ ...matchingVariant, quantity: item.quantity, bundle, bundleItem: item }] : []
          })
        })
      },
      customizationsTotals () {
        // The total of the 'original' prices - uses the compare at price if set, or defaults to the price otherwise.
        const compareAtTotal = this.selectedCustomizationProducts.reduce((total, variant) => {
          const price = typeof variant.compare_at_price === 'number' ? variant.compare_at_price : variant.price
          return total + price
        }, 0)

        // The total of the current (or sale) prices
        const total = this.selectedCustomizationProducts.reduce((total, variant) => {
          const discountedPrice = this.getDiscountedPrice(variant)
          return total + discountedPrice
        }, 0)

        console.debug('Customizations Totals:', { compareAtTotal, total })

        return { compareAtTotal, total }
      },
      bundleTotals () {
        // The total of the 'original' prices - uses the compare at price if set, or defaults to the price otherwise.
        const compareAtTotal = this.selectedBundleProducts.reduce((total, variant) => {
          const price = typeof variant.compare_at_price === 'number' ? variant.compare_at_price : variant.price
          const subtotal = price * variant.quantity
          return total + subtotal
        }, 0)

        // The total of the current (inc. sale) prices
        const total = this.selectedBundleProducts.reduce((total, variant) => {
          const discountedPrice = this.getDiscountedPrice(variant)
          const subtotal = discountedPrice * variant.quantity
          return total + subtotal
        }, 0)

        console.debug('Bundle Totals:', { compareAtTotal, total })

        return { compareAtTotal, total }
      },
      /** Total of ALL secondary items (bundle items, chain, pairs etc.) */
      secondaryTotals () {
        return {
          compareAtTotal: this.customizationsTotals.compareAtTotal + this.bundleTotals.compareAtTotal,
          total: this.customizationsTotals.total + this.bundleTotals.total,
        }
      },

      /** The current variants totals */
      variantTotals () {
        const currentVariant = this.currentVariant
        if (!currentVariant) {
          return { compareAtTotal: 0, total: 0 }
        }

        const compareAtTotal = typeof currentVariant.compare_at_price === 'number' ? currentVariant.compare_at_price : currentVariant.price
        const total = this.getDiscountedPrice(currentVariant)

        console.debug('Variant Totals:', { compareAtTotal, total })

        return { compareAtTotal, total }
      },

      /** Return the total price, _excluding_ discounts */
      totalPrice () {
        // Original price (using compare at if we have it) NOT the discounted price
        const basePrice = this.variantTotals.compareAtTotal

        // If we have a pair, then 2x it
        const price = this.isPairSelected ? basePrice * 2 : basePrice

        const subtotalPrice = price + this.secondaryTotals.compareAtTotal
        const totalPrice = subtotalPrice * this.quantity
        return totalPrice
      },
      /** Return the total discounted price */
      discountedPrice () {
        // We always want the price, not the compare_at price
        const basePrice = this.variantTotals.total

        // If we have a pair, then 2x it
        const price = this.isPairSelected ? basePrice * 2 : basePrice

        const subtotalPrice = price + this.secondaryTotals.total
        const totalPrice = subtotalPrice * this.quantity

        return totalPrice
      }
    },
    watch: {
      currentVariant: {
        immediate: true,
        handler (variant) {
          // We need to reset chain options, as the option variants differ across product variants
          // Set to the metafield default if we have it, otherwise set to if we have chain available
          const chainDefaultSelected = typeof metadata.defaults.chain_default_selected === 'boolean' ? metadata.defaults.chain_default_selected : this.isChainAvailable
          this.selectedCustomizations.chain = chainDefaultSelected
          this.setChainOptions(chainDefaultSelected)

          // Set current image index
          if (variant.featured_image && !this.isBundleProduct) this.currentImageIndex = variant.featured_image.position - 1

          // Set pairing option
          const variantsMetadata = metadata.variants[this.currentVariant.id]

          // Default should be single for sunburst charms
          if (variantsMetadata && variantsMetadata.family === 'Sunburst Charm') {
            this.selectedCustomizations.pairingOption = 'single'
          }

          this.handleKlaviyoEvents(variant)
        }
      },
      currentNonPersonalizedVariant: {
        immediate: true,
        handler (variant) {
          // Set variant search param
          const url = new URL(window.location)
          if (!variant) {
            url.searchParams.delete('variant')
          } else {
            url.searchParams.set('variant', variant.id)
          }

          window.history.replaceState({}, '', url)
        }
      },
      'selectedCustomizations.chain': {
        handler (value) {
          this.setChainOptions(value)
        }
      }
    },
    async mounted () {
      this.handleExternalLink()

      this.handleHintEvents()

      this.handlePreviewsEvents()

      // await this.fetchIncomingInventory()
    },
    methods: {
      toCapitalCase (string = '') {
        return string.replace('_', ' ').split('').reduce((str, c, i) => (i === 0) || (str.charAt(i - 1) === ' ') ? str.concat(c.toUpperCase()) : str.concat(c), '')
      },
      // Just a dumb fn to test something out
      toPlural (name, quantity = 1) {
        if (name.endsWith('s')) return name
        return `${ name }${ quantity > 1 ? 's' : '' }`
      },
      toggleSizeGuide (e) {
        e?.preventDefault()
        e?.stopImmediatePropagation?.()

        const event = new CustomEvent('apps:size-guide:toggle', {
          detail: {
            guide: this.matchingSizeGuide,
            chainGuide: this.matchingChainSizeGuide
          }
        })
        document.dispatchEvent(event)
      },
      /** Given a variant, get the discounted amount if any discounts apply. */
      getDiscountedPrice (variant) {
        // If on sale, then skip
        if (typeof variant.compare_at_price === 'number') {
          return variant.price
        }

        return this.appliedDiscounts.reduce((total, discount) => {
          const discountPercentage = discount.amount / 100
          const discountAmount = total * discountPercentage
          return total - discountAmount
        }, variant.price)
      },
      // Finds the Sterling Silver version of the current variant. The originalVariant
      getSterlingSilverVariant (variant) {
        const logger = this.createLogger('getSterlingSilverVariant')
        logger('Finding Sterling Silver variant of', variant.title, variant)

        let product = this.product
        let productOptions = this.optionsWithValues

        // No more than the current anyway, so return -
        // if (product.variants.length <= 1) return

        if (this.isBundleProduct) {
          // Find the formatted bundle variant, in case we are passing the basic data here
          const [matchingItem] = this.bundles.flatMap(bundle => {
            const matchingItem = bundle.items.find(item => {
              if (item.type === 'product') {
                return item.resource.variants.some(({ id }) => id === variant.id)
              }
              if (item.type === 'variant') {
                return item.resource.id === variant.id
              }

              return false
            })

            return matchingItem ? [matchingItem] : []
          })

          if (matchingItem) {
            const matchingProduct = matchingItem.type === 'product' ? matchingItem.resource : matchingItem.parent
            logger({ matchingProduct, matchingItem })

            if (matchingProduct) {
              product = matchingProduct
              productOptions = matchingItem.optionsWithValues
            }
          }

        }

        // Check for custom variant inventory maps
        const inventoryOverride = inventoryVariantsOverrides.get(variant.id)
        if (inventoryOverride?.sterlingSilver) {
          logger(`Found custom override for ID ${ variant.id }:`, inventoryOverride)

          // Find the variant that matches the override
          const matchingVariant = product.variants.find(variant => variant.id === inventoryOverride.sterlingSilver)
          if (matchingVariant) {
            logger(`Custom override for ID ${ variant.id } has matching variant - returning`)
            return matchingVariant
          }
        }

        // Creating a psuedo-selected options to match the current variant
        const wantedOptions = productOptions.map(option => {
          if (option.name.toLowerCase() === optionKeys.material.toLowerCase()) {
            return { ...option, selected: 'Sterling Silver' }
          }

          const variantOption = variant.options[option.position - 1]

          if (option.name.toLowerCase() === optionKeys.engraving.toLowerCase()) {
            // Only run if we match an engraved type
            // We want the non-engraved version
            if (engravingTypes.includes(variantOption)) {
              // Use the first variant option that ISN'T an engraved option
              const firstMatchingOption = option.values.find(value => !engravingTypes.includes(value))
              return { ...option, selected: firstMatchingOption }
            }
          }

          return { ...option, selected: variantOption }
        })

        logger('Wanting to match these options:', wantedOptions)

        // Find the variant that matches the wanted options
        const matchingVariant = product.variants.find(variant => {
          return wantedOptions.every(option => {
            const variantOption = variant.options[option.position - 1]
            return variantOption === option.selected
          })
        })

        logger('Found matching variant:', matchingVariant && matchingVariant.title, matchingVariant)

        return matchingVariant
      },
      // Returns a set of options, that should match the non-personalized version of the passed in variant
      getNonPersonalizedOptions (product, variant) {
        const logger = this.createLogger('getNonPersonalizedOptions')
        // TODO: We can use this in the sterling silver get function

        // So this should be selected options matching the passed-in variant, with all engraving types set to blank
        const selectedOptions = product.options.map((name, index) => {
          const variantOption = variant.options[index]

          // Check if the engraving option values includes our current variant option - if so, replace
          if (engravingTypes.includes(variantOption)) {
            // TODO: Very ineffecient way of doing this, so we should use optionsWithValues
            const allValues = Array.from(new Set(product.variants.map(variant => variant.options[index])))
            // Find first option that IS NOT the engraving type
            const nonEngravedOption = allValues.find(value => !engravingTypes.includes(value))
            return nonEngravedOption
          }

          return variantOption
        })

        logger(`Using selected options`, { selectedOptions, options: product.options })

        return selectedOptions
      },
      getNonPersonalizedVariant (variant, product) {
        const logger = this.createLogger('getNonPersonalizedVariant')
        logger(`Getting non-personalized variant of:`, variant.name, { variant, product })

        if (!product && this.isBundleProduct) {
          // We can skip the parent container, as we don't really need to check it
          const isBundleParent = this.product.variants.some(({ id }) => id === variant.id)
          if (isBundleParent) return variant

          const variantId = variant.id
          logger('No product, and we are a bundle - finding item', variantId)
          const [matchingVariant] = this.bundles.flatMap((bundle, b) => bundle.items.flatMap((item, i) => {
            const product = item.type === 'product' ? item.resource : item.parent

            // Find the parent product which includes our variant
            const includesVariant = product.variants.some(variant => variant.id === variantId)
            if (!includesVariant) return []

            // So this should be selected options matching the passed-in variant, with all engraving types set to blank
            const selectedOptions = this.getNonPersonalizedOptions(product, variant)

            // Now match the options
            return product.variants.filter(variant => selectedOptions.every((value, i) => value === variant.options[i]))
          }))

          logger('Bundle Condition', matchingVariant)
          if (!matchingVariant) console.warn('No matching variant found for this bundle item')
          return matchingVariant || variant
        }

        // Fallback if we don't pass it
        if (!product) product = this.product

        // Check for custom variant inventory maps
        const inventoryOverride = inventoryVariantsOverrides.get(variant.id)
        if (inventoryOverride?.nonPersonalized) {
          logger(`Found custom override for ID ${ variant.id }:`, inventoryOverride)

          // Find the variant that matches the override
          const matchingVariant = product.variants.find(variant => variant.id === inventoryOverride.nonPersonalized)
          if (matchingVariant) {
            logger(`Custom override for ID ${ variant.id } has matching variant - returning`)
            return matchingVariant
          }
        }

        // A clone of the variants options, but we ensure the engraving type is a non-personalized value
        const selectedOptions = this.getNonPersonalizedOptions(product, variant)

        logger('Using selectedOptions:', selectedOptions, { product, variant })

        // Now find the variant that matches these options
        const matchingVariant = product.variants.find(variant => selectedOptions.every((value, i) => value === variant.options[i]))

        return matchingVariant || variant
      },
      getShippingBadge (
        variants = []
      ) {
        const logger = this.createLogger('getShippingBadge')
        // ! Rudimentary function that returns a shipping message, based on the highest weighted message returned by the array of variants.
        // So in essence, the longer a shipping delay, the higher the weight, so the product with the longest shipping delay wins.

        // This is a list of ALL secondary materials in ALL our variants, regardless of whether they are selected or not.
        // Partly because we check the cart for all these variants
        const secondaryMaterialIds = secondaryMaterials.flatMap(material => {
          const productVariants = materialOption ? product.variants.flatMap(variant => variant.options[materialOption.position - 1] === material ? [variant.id] : []) : []

          const bundleVariants = this.bundles.flatMap(bundle => bundle.items.flatMap(item => {
            const product = item.type === 'product' ? item.resource : item.parent

            return product.variants.flatMap(variant => variant.options.includes(material) ? [variant.id] : [])
          }))

          return [...productVariants, ...bundleVariants]
        })

        logger(`Getting timelines for variants:`, { variants, secondaryMaterialIds })

        const messages = variants.map(variant => {
          const { enabledFrontEngraving, frontEngravingVariant, enabledBackEngraving, backEngravingVariant } = this.personalizationVariants || {}
          const personalized = enabledFrontEngraving ? frontEngravingVariant.id === variant.id : enabledBackEngraving ? backEngravingVariant.id === variant.id : false
          const personalizedVariant = enabledFrontEngraving ? frontEngravingVariant : enabledBackEngraving ? backEngravingVariant : null

          logger('Is personalized:', variant.title, personalized, { personalized, variant, personalizedVariant })

          // We always use this variant to check for inventory, as it is always the front variant we use
          const nonPersonalizedVariant = this.getNonPersonalizedVariant(variant)
          logger('Running for Variant:', variant.name, `(${ nonPersonalizedVariant.title })`)
          if (nonPersonalizedVariant) logger('Resolved Non-personalized Variant:', nonPersonalizedVariant.name, nonPersonalizedVariant)
          else logger('No non-personalized variant found')

          // I think this is right, but we can probably still use the nonPersonalizedVariant? As it will return the matching always
          const inventory = window.__initialData__.inventories.get(nonPersonalizedVariant.id)
          const currentSterlingSilverVariant = this.getSterlingSilverVariant(nonPersonalizedVariant, variant)
          const currentSterlingSilverVariantInventory = currentSterlingSilverVariant && window.__initialData__.inventories.has(currentSterlingSilverVariant.id) ? window.__initialData__.inventories.get(currentSterlingSilverVariant.id) : { quantity: 0 }

          logger('Variant Inventories', { variantId: nonPersonalizedVariant.id, inventory, currentSterlingSilverVariant, currentSterlingSilverVariantInventory })
          if (!inventory) {
            console.error('Missing inventory for variant', nonPersonalizedVariant.id)
          }

          /** Function to format the timeline */
          const weighted = (reason = '', weight, timeline) => {
            logger(`${ variant.name } - ${ weight } (${ reason })`, { variant, timeline, inventory, currentSterlingSilverVariant, currentSterlingSilverVariantInventory })
            return {
              weight,
              name: variant.name,
              description: typeof timeline === 'object' && timeline.description ? timeline.description.replace('null', '') : '',
              message: typeof timeline === 'object' ? timeline.timeline : typeof timeline === 'string' ? timeline : '',
              icon: typeof timeline === 'object' && timeline.icon ? timeline.icon : null,
              color: typeof timeline === 'object' && timeline.text_color ? timeline.text_color : 'var(--color-primary)',
              fontSize: typeof timeline === 'object' && timeline.font_size ? timeline.font_size : 'var(--font-size-base)',
              fontWeight: typeof timeline === 'object' && timeline.font_weight ? timeline.font_weight : 'var(--font-weight-bold)',
            }
          }

          // In theory this would override other checks for single products, but probably shouldn't for bundles.
          if (timelines.variants && timelines.variants[variant.id]) {
            return weighted('Variant has a custom timeline', 10, timelines.variants[variant.id])
          }

          if (timelines.meta) {
            return weighted('Timeline has custom meta', 10, timelines.meta)
          }

          /** Custom messages */
          const isDiamondLetter = product.tags.some(tag => tag.toLowerCase().includes('diamond letters'))
          if (isDiamondLetter) return weighted('Custom condition for diamond letter', 1, timelines.nextDay)

          const isAvailable = this.isBundleProduct ? variant.available : nonPersonalizedVariant.available
          if (!isAvailable) return weighted('Variant is not available, returning with 0 weight', 0)

          /**
           * A list of all this variants ID's, both personalized + non-personalized,
           * as the non-personalized variant should check to see if there are personalized variants
           * (as the personalized takes inventory from the non-personalized)
           *
           * TODO: We may also want to add these to the secondaryMaterials, so CG F+B can check the SS front fallback?
           */
          const itemsVariantsIds = new Set([variant.id, nonPersonalizedVariant.id])
          if (personalizedVariant) {
            itemsVariantsIds.add(personalizedVariant.id)
          }

          const itemQuantityInCart = window.cartApp.cart.items.reduce((total, item) => {
            if (itemsVariantsIds.has(item.variant_id)) return total + item.quantity
            return total
          }, 0)

          /** The quantity of items required, from the qty input */
          const itemQuantity = this.isPairProduct && this.selectedCustomizations.pairingOption === 'pair'
            ? this.quantity * 2
            : variant.bundle
              ? variant.quantity * this.quantity
              : this.quantity

          /** The total quantity of items needed, from both the qty input and the items in the cart. */
          const totalQuantityNeeded = itemQuantityInCart + itemQuantity

          logger('Quantities', { itemQuantityInCart, itemQuantity, totalQuantityNeeded })

          // Bit of a dumb fallback...
          const variantMaterialOptionIndex = materialOption ? materialOption.position - 1 : 0

          // if (!materialOption) return weighted(0)
          const material = variant.options[variantMaterialOptionIndex]
          const materialDefaultTimelines = timelines.materials.get(material.toLowerCase())
          logger('Materials', { material, materialDefaultTimelines })

          if (!materialDefaultTimelines || !materialDefaultTimelines.length) {
            return weighted('No default timeline for this material', 0)
          }

          const variantDefaultTimelines = materialDefaultTimelines.filter(timeline => timeline.personalized === personalized)
          if (!variantDefaultTimelines.length) {
            return weighted('No default timelines for this variant', 0)
          }

          logger('Timelines', { variantDefaultTimelines })

          // Get and set matching timelines, or fallback to defaults
          const [defaultStockedTimeline, ...stockedTimelines] = variantDefaultTimelines
            .filter(timeline => timeline.type.toLowerCase() === 'in stock')
            .toSorted((a, b) => {
              if (b.sku_number) return -1
              if (a.sku_number) return 1
              return 0
            })

          const [defaultOOSTimeline, ...oosTimelines] = variantDefaultTimelines
            .filter(timeline => timeline.type.toLowerCase() === 'out of stock')
            .toSorted((a, b) => {
              if (b.sku_number) return -1
              if (a.sku_number) return 1
              return 0
            })

          const [defaultFallbackTimeline, ...fallbackTimelines] = variantDefaultTimelines
            .filter(timeline => timeline.type.toLowerCase() === 'fallback stock')
            .toSorted((a, b) => {
              if (b.sku_number) return -1
              if (a.sku_number) return 1
              return 0
            })

          const [defaultPreorderTimeline, ...preorderTimelines] = variantDefaultTimelines
            .filter(timeline => timeline.type.toLowerCase() === 'preorder')
            .toSorted((a, b) => {
              if (b.sku_number) return -1
              if (a.sku_number) return 1
              return 0
            })

          const customTimelines = timelines.customOverrides
            ?.filter(timeline => timeline.sku === variant.sku)
            .toSorted((a, b) => a.order - b.order) ?? []

          const stockedTimeline = !stockedTimelines.length || !variant.sku
            ? defaultStockedTimeline
            : stockedTimelines.find(timeline => {
              return timeline.sku_number && variant.sku.includes(timeline.sku_number)
            }) || defaultStockedTimeline

          const oosTimeline = !oosTimelines.length || !variant.sku
            ? defaultOOSTimeline
            : oosTimelines.find(timeline => {
              return timeline.sku_number && variant.sku.includes(timeline.sku_number)
            }) || defaultOOSTimeline

          const fallbackTimeline = !fallbackTimelines.length || !variant.sku
            ? defaultFallbackTimeline
            : fallbackTimelines.find(timeline => {
              return timeline.sku_number && variant.sku.includes(timeline.sku_number)
            }) || defaultFallbackTimeline

          const preorderTimeline = !preorderTimelines.length || !variant.sku
            ? defaultPreorderTimeline
            : preorderTimelines.find(timeline => {
              return timeline.sku_number && variant.sku.includes(timeline.sku_number)
            }) || defaultPreorderTimeline

          logger('Matched Timelines', { stockedTimeline, oosTimeline, fallbackTimeline, preorderTimeline, customTimelines })

          // Preorder will override, right?
          if (this.isPreorderProduct && preorderTimeline) {
            return weighted('Custom preorder timeline for preorder product', 10, preorderTimeline)
          }

          // Get the total qty of secondary materials in cart (cavan gold) if they have 0 inventory, and will be using the SS inventory
          const secondaryMaterialsQty = secondaryMaterialIds.reduce((total, variantId) => {
            const cartItemQty = window.cartApp.cart.items.filter(item => item.variant_id === variantId).reduce((total, item) => total + item.quantity, 0)

            // If none in cart, return total (i.e. 0)
            if (!cartItemQty) return total

            const inventory = window.__initialData__.inventories.get(variantId)
            if (!inventory) return total

            // If the amount in cart is less than/equal to the current inventory, then we can return the total
            if (inventory.quantity >= cartItemQty) return total

            // ! If the amount in cart is > the available inventory, then return the difference that the SS will be used for
            const difference = cartItemQty - Math.max(inventory.quantity, 0)
            // logger({ cartItemQty, inventory: inventory.quantity, difference })
            return total + difference
          }, 0)

          logger('Secondary Materials Quantities', secondaryMaterialsQty)

          // If we have sterling silver, we need to check we don't already have cavan or rose gold
          // already in cart that use the sterling inventory
          if (material === 'Sterling Silver') {
            const updatedSterlingSilverInventory = currentSterlingSilverVariantInventory.quantity - secondaryMaterialsQty
            if (updatedSterlingSilverInventory >= totalQuantityNeeded) {
              return weighted('Sterling silver material, and current inventory is greater than total needed', 2, stockedTimeline)
            }
          }

          // If we have greater than one item in stock, we can ship next day
          if (material !== 'Sterling Silver' && inventory && inventory.quantity >= totalQuantityNeeded) {
            return weighted('Material is not sterling silver, but inventory is greater than needed', 2, stockedTimeline)
          }


          //! Here we need to do a generic fallback check? Could get complicated because we'd need to do comparisons above, so leave for now

          // If we have cavan gold or rose gold where inventory = 0, but we have sterling silver > 0, we can ship in 5 days

          const isSecondaryMaterial = secondaryMaterialIds.includes(nonPersonalizedVariant.id)
          const cartSterlingSilver = currentSterlingSilverVariant ? window.cartApp.cart.items.reduce((total, item) => item.variant_id === currentSterlingSilverVariant.id ? total + item.quantity : total, 0) : 0
          // Get the total available of the sterling silver inventory, subtracting any sterling or secondary material items already in cart
          const totalSterlingSilverInventory = currentSterlingSilverVariantInventory ? currentSterlingSilverVariantInventory.quantity - cartSterlingSilver - secondaryMaterialsQty : 0
          logger('Sterling Silver Fallback Checks', { isSecondaryMaterial, fallbackTimeline, currentSterlingSilverVariant, currentSterlingSilverVariantInventory, totalSterlingSilverInventory, itemQuantity, cartSterlingSilver, secondaryMaterialsQty, secondaryMaterialIds })

          if (fallbackTimeline && isSecondaryMaterial && totalSterlingSilverInventory >= itemQuantity) {
            return weighted(`Fallback timeline; Fallback material (sterling silver) is greater than total needed`, 3, fallbackTimeline)
          }

          // Custom timelines - these check incoming inventory, so run after the available + fallback checks
          logger('Checking for custom incoming inventory timelines')
          if (customTimelines) {
            logger('Custom Timelines:', customTimelines)

            // Only continue if we allow overselling
            if (inventory.policy === 'continue') {
              // We can have multiple incoming, at different times, so map through each (presumably sorted above by order)
              for (const customTimeline of customTimelines) {
                logger(`Checking custom timeline: ${ customTimeline.relativeTimeline }`)

                // Only continue if we have incoming quantity
                if (typeof customTimeline.incomingQuantity === 'number' && customTimeline.incomingQuantity > 0) {
                  // Now we do (incoming - available) to get how much incoming we have left (although we should have already returned above by this point)
                  // If inventory available is >= 0, then return the full incoming inventory (as we haven't used any yet).
                  // If incoming available (incoming - available) is >= 0 the return incoming available left, otherwise return 0
                  const incomingAvailable = inventory.quantity >= 0 ? customTimeline.incomingQuantity : Math.max(customTimeline.incomingQuantity - Math.abs(inventory.quantity), 0)

                  // TODO: Might need to cover fallbacks too?
                  // We do `(totalQuantityNeeded - available)` to see how much the available can cover, and how much the incoming needs to cover
                  const totalQuantityNeededAfterAvailable = totalQuantityNeeded - inventory.quantity
                  // Total quantity after removing fallback materials inventory
                  const totalQuantityNeededAfterAvailableAndFallbacks = fallbackTimeline && isSecondaryMaterial && totalSterlingSilverInventory ? totalQuantityNeededAfterAvailable - totalSterlingSilverInventory : totalQuantityNeededAfterAvailable
                  logger('Total needed after removing available:', { totalQuantityNeeded, totalQuantityNeededAfterAvailable, totalQuantityNeededAfterAvailableAndFallbacks, available: inventory.quantity, incoming: customTimeline.incomingQuantity, incomingAvailable })

                  if (incomingAvailable >= totalQuantityNeededAfterAvailable) {
                    return weighted('Incoming inventory available is greater than qty needed', 4, {
                      ...customTimeline,
                      text_color: 'var(--color-danger)',
                      font_size: 'var(--font-size-base)',
                      font_weight: 'var(--font-weight-heavy)',
                    })
                  }
                }
              }
            }
          }

          return oosTimeline ? weighted('OOS timeline found', 5, oosTimeline) : weighted('No conditions met, falling back to 0 weight', 0)
        })

        const [highestMessage] = messages.sort((a, b) => b.weight - a.weight)

        logger('Resolved Messages', {
          highestMessage,
          messages
        })

        return highestMessage && highestMessage.weight ? highestMessage : null
      },
      getSizedImageUrl (src, size) {
        return window.Shopify.Image.getSizedImageUrl(src, size)
      },
      getBackgroundColor (value) {
        const handle = value.toLowerCase().trim().replace(/ /g, '-')
        return window.__initialData__.colorSwatches.get(handle)
      },
      getExtraPrice (variants, current = {}) {
        const [cheapest] = variants // Previously sorted by price
        return current.price - cheapest.price
      },
      hasDiscountBadge (title) {
        return this.appliedDiscounts.some(discount => discount.title === title)
      },
      filteredOptions (options) {
        return options.filter(option => {
          if (option.name === 'Title') {
            return !option.values.includes('Default Title')
          }

          return true
        })
      },
      // Update general toggles state
      toggle (key, value) {
        this.toggles[key] = typeof value === 'boolean' ? value : !this.toggles[key]
      },
      // Updates the selected options to use the preset engraving option
      togglePresetOption (state) {
        const isPresetSelected = typeof state === 'boolean' ? !state : this.selectedOptions[optionKeys.engraving] === engravingKeys.options.frontPreset

        if (isPresetSelected) {
          // For some options we don't have front engraving, only front + back. So if we don't have a front option, then fallback to it
          this.selectedOptions[optionKeys.engraving] = metadata.details?.front_variant_option_name ?? engravingKeys.options.front
        } else {
          this.selectedOptions[optionKeys.engraving] = engravingKeys.options.frontPreset
        }
      },
      toggleLightbox () {
        this.lightboxActive = !this.lightboxActive
      },
      setSelectedBundleOption (bundleIndex, itemIndex, key, value) {
        this.selectedBundleOptions[bundleIndex][itemIndex][key] = value
      },
      setSelectedOption (key, value) {
        this.selectedOptions[key] = value
      },
      /**
       * Create the initial chain options - runs when we change the current variant, to reset options available.
       * It also uses the selected parent material as the initial chain material, and the other computed functions
       * handle filtering if we manually set the chain material.
       */
      setChainOptions (selected = true) {
        const logger = this.createLogger('setChainOptions')

        const selectedParentMaterial = this.selectedOptions[optionKeys.material]
        logger('Context', { selected, selectedParentMaterial }, this.selectedOptions, this.selectedCustomizations)

        /**
         * Always reset the selected chain material to the selected parent material,
         * unless we are a 10K variant, when we should use the 14K material option.
         *
         * Temporary, and not configured via section settings as likely to only need for this particular material.
         */
        if (selectedParentMaterial === materialValues.gold10K) {
          logger('Selected 10K, using 14K alias')
          this.selectedCustomizations.chainMaterial = materialValues.gold14K
        } else {
          this.selectedCustomizations.chainMaterial = selectedParentMaterial
        }

        if (!selected || !this.availableChainOptionProducts.length) {
          logger('Chain was not selected, or there are no available chain products, so setting options to null.')
          this.selectedCustomizations.chainSize = null
          this.selectedCustomizations.chainStyle = null
          return
        }

        const { defaults } = metadata
        if (defaults.chain_default) {
          logger('We have chain defaults, so finding best match', { defaults })
          const matchingVariant = this.availableChainOptionProducts.find(({ options }) => {
            const matchesDefaultSize = options.size === defaults.chain_default
            const matchesMaterial = options.material === selectedParentMaterial
          })

          if (matchingVariant) {
            logger('We have a matching chain default, so setting options', { matchingVariant })
            this.selectedCustomizations.chainSize = matchingVariant.options.size
            this.selectedCustomizations.chainStyle = matchingVariant.options.style
            return
          }
        }

        logger('No defaults, so finding best match')
        // As we are re-setting, filter for material matching the selected parent material
        const [firstVariant] = this.availableChainOptionProducts.sort((a, b) => a.price - b.price).filter(({ options }) => {
          return options.material === selectedParentMaterial && options.size !== '16"' && options.size !== '18"'
        })

        if (firstVariant) {
          logger('We have a matching variant, so setting options', { firstVariant })

          this.selectedCustomizations.chainSize = firstVariant.options.size
          this.selectedCustomizations.chainStyle = firstVariant.options.style
        }
      },
      setSelectedCustomizations (key, value, index) {
        if (this.selectedCustomizations[key] === value) {
          // If choosing the same option, then close the preview
          this.setActiveOptionPreview(key, false)
        } else {
          // Else, set customization, and open preview if not already open
          this.selectedCustomizations[key] = value
          this.setActiveOptionPreview(key, true)
        }
      },
      selectFirstAvailableChainVariant() {
        const logger = this.createLogger('selectFirstAvailableChainVariant')
        if (!this.selectedCustomizations.chainSize || !this.selectedCustomizations.chainStyle) {
          return
        }

        const [firstVariant] = this.availableChainOptionProducts.sort((a, b) => a.price - b.price).filter(({ options }) => {
          return this.selectedCustomizations.chainMaterial === options.material && 
          this.selectedCustomizations.chainSize === options.size 
        })

        if (firstVariant) {
          logger('We have a matching variant, so setting options', { firstVariant })

          this.selectedCustomizations.chainSize = firstVariant.options.size
          this.selectedCustomizations.chainStyle = firstVariant.options.style
        }
      },
      // Toggle the current option preview popovers
      setActiveOptionPreview (key, value) {
        const newValue = typeof value === 'boolean' ? value : !this.previewsActive[key]
        this.previewsActive[key] = newValue
      },
      /** Use to add handlers for toggling state on popup clicks. */
      onClickOutside (handler = () => {}, ignoreSelectors = []) {
        document.addEventListener('click', e => {
          if (e.target instanceof Element) {
            const hasMatchingIgnore = ignoreSelectors.some(selector => !!e.target.closest(selector))
            if (!hasMatchingIgnore) {
              handler()
            }
          }
        })
        document.addEventListener('touchend', e => {
          if (e.target instanceof Element) {
            const hasMatchingIgnore = ignoreSelectors.some(selector => !!e.target.closest(selector))
            if (!hasMatchingIgnore) {
              handler()
            }
          }
        })
      },
      // Handle events for the option preview popovers
      handlePreviewsEvents () {
        // this.onClickOutside(() => {
        //   this.toggle('sizeGuide', false)
        // }, ['[data-name="SizeGuideTrigger"]'])
        this.onClickOutside(() => {
          this.toggle('shippingBadgeTooltip', false)
        }, ['[data-name="ShippingBadgeTrigger"]'])

        // If we click outside the option buttons or material options, we should close.
        // TODO: Fix if we have conflicts - maybe we can prevent bubbling in the setCustomization handler?
        document.addEventListener('click', e => {
          if (e.target instanceof Element) {
            const closestChainStylePreview = e.target.closest('[data-name="ChainStylePreview"]')
            if (closestChainStylePreview) {
              this.setActiveOptionPreview('chainStyle', false)
            }

            const closestMaterialGroup = e.target.closest('[data-name="Material"]')
            const closestChainStyleGroup = e.target.closest('[data-name="ChainStyle"]')
            if (!closestMaterialGroup && !closestChainStyleGroup) {
              this.setActiveOptionPreview('chainStyle', false)
            }
          }
        })

        document.addEventListener('touchend', e => {
          if (e.target instanceof Element) {
            const closestChainStylePreview = e.target.closest('[data-name="ChainStylePreview"]')
            if (closestChainStylePreview) {
              this.setActiveOptionPreview('chainStyle', false)
            }

            const closestMaterialGroup = e.target.closest('[data-name="Material"]')
            const closestChainStyleGroup = e.target.closest('[data-name="ChainStyle"]')
            if (!closestMaterialGroup && !closestChainStyleGroup) {
              this.setActiveOptionPreview('chainStyle', false)
            }
          }
        })
      },
      setActiveSection (type) {
        if (this.activeActionSection === type) this.activeActionSection = ''
        else this.activeActionSection = type
      },
      setActiveInformation (type) {
        if (this.activeInformationSection === type) this.activeInformationSection = ''
        else this.activeInformationSection = type
      },
      openPopup (type) {
        productPopupsApp.open(type)
      },
      scrollThumbnails () {
        this.$refs.thumbnails.scrollBy(0, 150)
      },
      createLogger (tag) {
        return (...messages) => console.debug(`[Product]${ tag ? `(${ tag })` : '' }`, ...messages)
      },
      /** Creates the line item inputs for all selected variants, including the quantity and any properties. */
      createLineItems (
        /**
         * To replace line items with custom variants, add them here.
         * Should be an array of objects, with the object being the variant object,
         * and a properties field for any properties to add.
         *
         * @example [{ id: 123, ..., properties: { For: 'ABC123' } }]
         */
        replacements = []
      ) {
        const logger = this.createLogger('createLineItems')
        if (replacements.length) logger('Got replacements arg:', replacements)

        const handleReplacements = (variant, product) => {
          if (!product) {
            logger('No product found for variant', variant.name)
            return variant
          }

          logger(`Finding replacements for variant ${ variant.name }`, { variant, product })

          const productsVariantsIds = product.variants.map(variant => variant.id)

          const replacement = replacements.find(repVar => productsVariantsIds.includes(repVar.id))
          if (replacement) logger(`Found replacement ${ replacement.name } for variant ${ variant.name }`, { variant, replacement })

          return replacement || variant
        }

        // Inputs
        // const variant = this.currentVariant
        const variant = handleReplacements(this.currentVariant, this.product)
        const customizationVariants = this.selectedCustomizationProducts
        const bundleVariants = this.selectedBundleProducts.map(variant => {
          if (!variant.bundleItem) {
            logger('!WARNING', `Missing bundleItem property from variant ${ variant.name }`)
            return variant
          }

          const product = variant.bundleItem.type === 'variant' ? variant.bundleItem.parent : variant.bundleItem.resource

          const replacement = handleReplacements(variant, product)
          return { ...variant, ...replacement, bundleItem: variant.bundleItem, bundle: variant.bundle }
        })

        // Create variant & option line items
        const createId = () => Math.random().toString().substring(2)

        logger('Creating line items for', variant.name, { variant, bundleVariants, customizationVariants })

        const variantId = variant.id
        const variantName = variant.name

        // `let`, as we edit this later
        let quantity = this.quantity

        // Create an array of all product options, with needed keys & values
        const options = customizationVariants.map(optionVariant => ({
          optionId: createId(),
          ...optionVariant
        }))

        // Create the line item properties object - add product options, and the generated option key (used for cart mapping) _only_ if its price is > 0
        let properties = Object.fromEntries(options.flatMap(option => {
          const propertyKey = (option.type === 'chain' ? 'Chain' : option.title).toLowerCase().replace(' ', '_')
          const property = [this.toCapitalCase(propertyKey), option.name]
          const optionKey = [`_option_${ propertyKey }_id`, option.optionId]

          const properties = option.price > 0 ? [property, optionKey] : [property]

          // If the chain material is different to the product material, then add the property to indicate it
          if (option.type === 'chain' && option.options?.material && !variant.options.includes(option.options.material)) {
            properties.push([this.toCapitalCase('chain material'), option.options.material])
          }

          return properties
        }))

        properties._variant_name = variantName
        // May be overridden later
        if (metadata.family) properties._variant_family = metadata.family
        if (metadata.vector) properties._option_vector = metadata.vector

        // Merge replacement properties
        if (variant.properties) {
          properties = {
            ...properties,
            ...variant.properties
          }
        }

        if (this.giftnote) properties['Gift Note'] = this.giftnote


        const shippingBadge = this.getShippingBadge(this.isBundleProduct ? bundleVariants : [variant])
        logger('Shipping Badge', shippingBadge)

        // Add shipping timeline
        if (shippingBadge && shippingBadge.message) {
          // Comes from cart.js
          const key = shippingTimelineName || 'Shipping Timeline'

          const [formattedMessage] = shippingBadge.message.split(':').reverse()
          // const message = replacements.reduce((message, replacement) => message.replace(replacement, '').trim(), this.shippingBadge.message)

          properties[key] = formattedMessage || shippingBadge.message

          // Add support for new checkout extensions - uses hidden attribute
          properties['_shipping_timeline'] = formattedMessage || shippingBadge.message
        }

        // Add personalization vector info
        if (this.isPersonalizable) {
          const { personalizationSide } = this.personalizationVariants
          if (personalizationSide) properties._option_personalization_side = personalizationSide

          const personalizationOptions = metadata.vector && window.personalizationApp.getPreviewOptions(metadata)
          if (personalizationOptions) {
            properties._option_vector = personalizationOptions.name || ''
            properties._option_vector_family = personalizationOptions.family || ''
          }
        }

        // If pairing
        if (this.isPairProduct) {
          properties[pairingOptionName] = this.isBundleProduct
            ? pairingOptionValues.BUNDLE
            : this.selectedCustomizations.pairingOption === 'pair'
              ? pairingOptionValues.PAIR
              : pairingOptionValues.SINGLE

          if (this.selectedCustomizations.pairingOption === 'pair') {
            quantity = quantity * 2
          }
        }

        // If sale item
        if (this.product.tags.includes('sale')) {
          properties[saleItemPropertyName] = true
        }

        // If Gift Card
        if (metadata.isGiftCard) {

          if (this.giftcard.message) properties["Gift Note"] = this.giftcard.message

          // This date always needs to be formatted as `YYYY-MM-DD`.
          const formatToISODate = (dateString) => {
            const date = dateString ? new Date(dateString) : new Date()
            // As we know the ISO is _always_ a specific format, we can just split at the T divider
            const [ISODate] = date.toISOString().split('T')
            return ISODate
          }

          if (this.giftcard.sendToSelf) {
            properties['_Send To Self'] = true
            properties["Delivery Date"] = formatToISODate()
          } else {
            properties["Delivery Date"] = formatToISODate(this.giftcard.date)
            properties["Recipient Name"] = this.giftcard.name.trim()
            properties["Recipient Email"] = this.giftcard.email.toLowerCase().trim()
          }
        }

        // Create the product option line items for options (i.e. chain) where price is > 0
        const optionLineItems = options.filter(option => option.price > 0).map(({ id, optionId, name }) => {
          const properties = {
            _option_id: optionId,
            _variant_name: name,
            For: variantName
          }

          return {
            id,
            quantity,
            properties
          }
        })

        // Create bundle line items
        if (bundleVariants.length) {
          properties._option_bundle_item = true
          properties._option_bundle_primary = true
          properties._option_bundle_id = createId()
          properties._option_bundle_name = product.title
          properties._option_bundle_product_id = product.id
          properties._variant_family = metadata.family
          properties.Bundle = product.title

          // We may need to update this if we ever want to set multiple bundles
          const [initialVariant] = bundleVariants
          properties._option_bundle_type = initialVariant.bundle.type
        }

        const bundleLineItems = bundleVariants.map(bundleVariant => {
          const bundleProduct = bundleVariant.bundleItem.type === 'variant' ? bundleVariant.bundleItem.parent : bundleVariant.bundleItem.resource
          const bundleProductMetadata = bundleVariant.bundleItem.metadata.get(bundleProduct.id)

          const replacedProperties = bundleVariant.properties || {}

          const itemProperties = {
            ...replacedProperties,
            _option_bundle_item: true,
            _option_bundle_id: properties._option_bundle_id,
            _option_bundle_parent: variantId,
            _option_bundle_parent_name: variantName,
            _option_bundle_parent_family: metadata.family,
            _option_bundle_name: product.title,
            _option_bundle_type: bundleVariant.bundle.type,
            _option_bundle_item_quantity: bundleVariant.bundleItem.quantity,
            Bundle: product.title,
            _variant_name: bundleVariant.name,
            _option_vector: bundleProductMetadata?.personalization_preview,
            _option_vector_family: bundleProductMetadata?.family
          }

          if (bundleVariant.bundleItem.quantity > 1) {
            itemProperties[pairingOptionName] = bundleVariant.bundleItem.pairingText
          }

          const totalQuantity = bundleVariant.quantity * quantity

          logger(`Adding ${ totalQuantity } (${ bundleVariant.quantity } original) of ${ bundleVariant.name } to cart`, itemProperties, bundleProductMetadata)

          return {
            id: bundleVariant.id,
            properties: itemProperties,
            quantity: totalQuantity
          }
        })

        const variantLineItem = { id: variantId, properties, quantity }

        logger('[CreateLineItems]', { optionLineItems, bundleLineItems, variantLineItem })

        return { optionLineItems, bundleLineItems, variantLineItem }
      },
      resetSelectedOptions () {
        const initial = JSON.parse(JSON.stringify(this.initial))

        this.selectedOptions = initial.selectedOptions
        this.selectedBundleOptions = initial.selectedBundleOptions
      },
      /** If we come from an external (whitelisted) source, and we have a personalized variant selected, then automatically open the personalization popup */
      handleExternalLink () {
        const externalSources = ['facebook', 'instagram']

        const utmSource = originalSearchParams.get('utm_source')
        const variantId = originalSearchParams.has('variant') ? Number(originalSearchParams.get('variant')) : undefined

        if (!utmSource || !variantId || !externalSources.includes(utmSource)) return

        const { enabledFrontEngraving, enabledBackEngraving, backEngravingVariant, frontEngravingVariant } = this.personalizationVariants

        // Usually we add back, so we check for that first
        if (enabledBackEngraving && backEngravingVariant.id === variantId) {
          this.personalize()
        }

        // Here we have no back, and we may or may not have front engraving.
        if (enabledFrontEngraving && frontEngravingVariant.id === variantId) {
          this.personalize()
        }
      },
      /** Send events to Klaviyo, with resolved data (instead of using liquid data) */
      handleKlaviyoEvents (variant) {
        if (!window.klaviyo) return

        const featuredImage = this.isBundleProduct || !variant.featured_image ? this.product.featured_image : variant.featured_image
        const featuredImageSrc = typeof featuredImage === 'object' ? featuredImage.src : featuredImage

        const item = {
          ProductName: this.product.title,
          Title: variant.name,
          ProductID: this.product.id,
          SKU: variant.sku,
          ItemId: variant.id,
          // Categories: klaviyoItem.Categories,
          ImageUrl: featuredImageSrc ? `https:${ featuredImageSrc }` : undefined,
          Url: window.location.href,
          Price: formatMoney(this.discountedPrice || this.totalPrice),
          CompareAtPrice: formatMoney(this.totalPrice),
          Brand: this.product.vendor,
          Metadata: {
            Brand: this.product.vendor,
            Price: formatMoney(this.discountedPrice || this.totalPrice),
            CompareAtPrice: formatMoney(this.totalPrice),
          }
        }

        window.klaviyoItem = item
      },
      addToWaitlist () {
        const variant = this.currentVariant
        window.productWishlistApp.open(product, variant)
      },
      personalize () {
        const options = this.selectedCustomizationProducts

        const lineItems = this.createLineItems()

        const personalizationVariants = this.personalizationVariants
        if (!personalizationVariants) return

        window.personalizationApp.start({
          personalizationVariants,
          personalizationResources: this.personalizationResources,
          options,
          lineItems,
        })

        this.quantity = 1
      },
      validateGiftCard () {
        if (!metadata.isGiftCard) return true

        if (!this.giftcard) {
          console.error('Missing this.giftcard object')
          return false
        }

        if (this.giftcard.sendToSelf) return true

        // Pretty basic validation
        return Boolean(this.giftcard.name && this.giftcard.date && this.giftcard.email && this.giftcard.email.includes('@'))
      },
      async addToCart () {
        this.state = { loading: true, success: false, error: false }

        // Gift Card Checks
        if (metadata.isGiftCard) {
          const valid = this.validateGiftCard()
          this.giftcard.invalid = !valid

          if (!valid) {
            this.state = { loading: false, success: false, error: 'Please fill out required gift card fields.' }
            return
          }
        }

        try {
          const { optionLineItems, variantLineItem, bundleLineItems } = this.createLineItems()
          const items = [variantLineItem, ...bundleLineItems, ...optionLineItems]

          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items })
          })

          if (!response.ok) {
            const error = await response.json()
            throw new Error(error ? error.description : 'Failed to add item to cart.')
          }

          await cartApp.showAddedLines(await response.json())
          if (window.upsellApp) window.upsellApp.start(this.currentVariant)
          else cartApp.open()

          this.quantity = 1

          this.state = { loading: false, success: true, error: false }

          // Klaviyo tracking
          window.klaviyo.push(['track', 'Added to Cart', window.klaviyoItem])
        } catch (error) {
          console.error(error)
          this.state = { loading: false, success: false, error: error.message }
        } finally {
          setTimeout(() => {
            this.state = { loading: false, success: false, error: false }
          }, 2500)
        }
      },
      /** Uses the Drop A Hint 2.0 app
       * They don't seem to offer any option to custom place the trigger (whyy??)
       * so we hide it, then trigger it with our own button.
       */


        dropAHint () {
          const hintTriggerEl = document.querySelector('#dropahint')

          if (!hintTriggerEl) return

          // ❌ OLD (problem causing double popup)
          // hintTriggerEl.click()

          // ✅ FIX: prevent bubbling + controlled trigger
          const event = new MouseEvent('click', {
            bubbles: false,
            cancelable: true
          })

          hintTriggerEl.dispatchEvent(event)
        },


      /*
      dropAHint () {
        const hintTriggerEl = document.querySelector('#dropahint')
        if (hintTriggerEl) hintTriggerEl.click()
      },*/
      /** Part of the above dropAHint functionality:
       * They manage to render the button inside our personalization button for some reason, so clicking it
       * triggers the personalization modal -
       * we stop that propogation here
       */
      handleHintEvents () {
        const targetNode = document.querySelector('.product-details')
        if (!targetNode) return

        const observer = new MutationObserver((mutationList, observer) => {
          for (const mutation of mutationList) {
            if (mutation.target.id === 'dropahint') {
              mutation.target.addEventListener('click', e => {
                // Stop propogation to parents
                e.stopPropagation()
              })

              observer.disconnect()
            }
          }
        })

        observer.observe(targetNode, { childList: true, subtree: true })
      },
      /** If we have a matching custom timeline at all, then fetch the incoming inventory levels from our custom API. */
      async fetchIncomingInventory () {
        const logger = (...messages) => console.debug('[Incoming Inventory]', ...messages)

        try {
          const variantSkus = product.variants.flatMap(variant => variant.sku ? [variant.sku] : [])
          const hasCustomTimeline = timelines?.customOverrides?.some(timeline => variantSkus.includes(timeline.sku))

          logger('Conditions:', { variantSkus, hasCustomTimeline })

          if (!hasCustomTimeline) {
            logger('No custom timeline, skipping.')
            return
          }

          const endpoint = `https://kc-api-custom-timelines.sivart.workers.dev/api/inventory/${ this.product.id }`
          const response = await fetch(endpoint)

          const json = await response.json()
          if (response.status === 404) {
            return logger(json.message)
          }

          if (!response.ok) {
            logger('API Error:', json)
            throw new Error(json.message)
          }

          logger('Got API response:', json)

          if (Array.isArray(json)) {
            this.inventory.incoming = new Map(json)
          }
        } catch (error) {
          logger('We got an error 😢', error.message)
          console.error(error)
        } finally {
          this.inventory.loading = false
        }
      }
    }
  })
}

// Product app
window.productApp = initProductApp('#product', window.__productData__)


/*
const dropHintBtn = document.querySelector('#dropahint_btn')
const galleryEl = document.querySelector('.product-gallery-lightbox')

if (dropHintBtn && galleryEl) {
  dropHintBtn.addEventListener('click', e => {
    e.stopPropagation()

    galleryEl.classList.toggle('hide')
  })
}



document.addEventListener('click', e => {
  const closeBtn = e.target.closest('.u-close-button')

  if (closeBtn) {
    e.stopPropagation()

    document.body.style.overflow = 'auto'

    if (galleryEl) {
      galleryEl.classList.remove('hide')
       galleryEl.classList.remove('active')
    }
  }
})*/